'use client';

/**
 * SearchAiz BaseTile Component
 *
 * Abstract base tile that provides consistent structure for all result types.
 * Each specific tile (DocumentTile, CallTile, etc.) extends this base.
 *
 * Features:
 * - Compact and expanded display modes
 * - Score and source badge display
 * - Keyboard navigation support
 * - Action menu with common operations
 */

import React, { forwardRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import {
  ExternalLink,
  Bookmark,
  Share2,
  Copy,
  Eye,
  MoreHorizontal,
  type LucideIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { KnowledgeSectionMarkdown } from '@/components/knowledge-base/KnowledgeSectionMarkdown';

import type {
  SearchResult,
  BaseTileProps,
  TileAction,
  TileDisplayMode,
} from '../types';
import { getSourceBadgeColor, getSourceIcon, getSourceIconColor } from '../utils/dataSourceRegistry';

// =============================================================================
// SCORE BADGE COMPONENT
// =============================================================================

interface ScoreBadgeProps {
  score: number;
  rerankScore?: number;
  className?: string;
}

export function ScoreBadge({ score, rerankScore, className }: ScoreBadgeProps) {
  const displayScore = rerankScore ?? score;
  const percentage = Math.round(displayScore * 100);

  // Color based on score
  const getScoreColor = (pct: number) => {
    if (pct >= 80) return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    if (pct >= 60) return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        'text-xs font-mono tabular-nums',
        getScoreColor(percentage),
        className
      )}
    >
      {percentage}%
      {rerankScore !== undefined && (
        <span className="ml-1 opacity-60" title="AI reranked">✨</span>
      )}
    </Badge>
  );
}

// =============================================================================
// SOURCE BADGE COMPONENT
// =============================================================================

interface SourceBadgeProps {
  source: string;
  className?: string;
}

export function SourceBadge({ source, className }: SourceBadgeProps) {
  const Icon = getSourceIcon(source);
  const badgeColor = getSourceBadgeColor(source);

  return (
    <Badge
      variant="secondary"
      className={cn('text-xs gap-1', badgeColor, className)}
    >
      <Icon className="w-3 h-3" />
      <span className="capitalize">{source.replace(/_/g, ' ')}</span>
    </Badge>
  );
}

// =============================================================================
// ACTION MENU COMPONENT
// =============================================================================

interface ActionMenuProps {
  result: SearchResult;
  onAction?: (action: TileAction) => void;
}

export function ActionMenu({ result, onAction }: ActionMenuProps) {
  const handleAction = useCallback(
    (type: TileAction['type']) => {
      if (!onAction) return;

      switch (type) {
        case 'view':
          if (result.url) {
            onAction({ type: 'view', url: result.url });
          }
          break;
        case 'bookmark':
          onAction({ type: 'bookmark', resultId: result.id });
          break;
        case 'share':
          onAction({ type: 'share', resultId: result.id });
          break;
        case 'copyLink':
          if (result.url) {
            onAction({ type: 'copyLink', url: result.url });
          }
          break;
        case 'preview':
          onAction({ type: 'preview', resultId: result.id });
          break;
      }
    },
    [result, onAction]
  );

  return (
    <DropdownMenu look="standard">
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {result.url && (
          <DropdownMenuItem onClick={() => handleAction('view')}>
            <ExternalLink className="icon" aria-hidden="true" />
            Open
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => handleAction('preview')}>
          <Eye className="icon" aria-hidden="true" />
          Preview
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleAction('bookmark')}>
          <Bookmark className="icon" aria-hidden="true" />
          Bookmark
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleAction('share')}>
          <Share2 className="icon" aria-hidden="true" />
          Share
        </DropdownMenuItem>
        {result.url && (
          <DropdownMenuItem onClick={() => handleAction('copyLink')}>
            <Copy className="icon" aria-hidden="true" />
            Copy Link
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// =============================================================================
// BASE TILE COMPONENT
// =============================================================================

export interface BaseTileRenderProps {
  /** Icon to display */
  icon: LucideIcon;
  /** Icon color class */
  iconColor?: string;
  /** Primary title */
  title: string;
  /** Secondary subtitle */
  subtitle?: string;
  /** Preview/description text */
  preview?: string;
  /** Extracted KB snippets are markdown regardless of the source file type. */
  previewMarkdown?: boolean;
  /** Metadata items to display */
  metadata?: Array<{
    label: string;
    value: string | number;
    icon?: LucideIcon;
  }>;
  /** Tags to display */
  tags?: string[];
  /** Custom right-side content */
  rightContent?: React.ReactNode;
  /** Custom bottom content (expanded mode only) */
  bottomContent?: React.ReactNode;
}

interface BaseTileInternalProps extends BaseTileProps {
  /** Render function for customizing tile content */
  renderContent: (result: SearchResult) => BaseTileRenderProps;
}

/**
 * Internal BaseTile implementation with render props pattern
 */
const BaseTileInternal = forwardRef<HTMLDivElement, BaseTileInternalProps>(
  function BaseTileInternal(
    {
      result,
      mode,
      showScore = false,
      showSource = false,
      highlighted = false,
      listboxOption,
      gridCell,
      onClick,
      onAction,
      className,
      renderContent,
    },
    ref
  ) {
    const content = renderContent(result);
    const Icon = content.icon;

    return (
      <div
        ref={ref}
        id={gridCell?.id ?? listboxOption?.id}
        role={gridCell ? 'gridcell' : listboxOption ? 'option' : 'button'}
        aria-selected={gridCell?.selected ?? listboxOption?.selected}
        tabIndex={listboxOption || gridCell ? -1 : 0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
        className={cn(
          'group relative flex gap-3 rounded-lg border p-3 transition-all cursor-pointer',
          'bg-slate-900/50 border-slate-800 hover:border-slate-700',
          'focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50',
          highlighted && 'ring-2 ring-purple-500/50 border-purple-500/50 bg-slate-800/50',
          mode === 'expanded' && 'p-4',
          className
        )}
      >
        {/* Icon */}
        <div
          className={cn(
            'flex-shrink-0 rounded-lg flex items-center justify-center',
            'bg-slate-800/50 border border-slate-700/50',
            mode === 'compact' ? 'w-10 h-10' : 'w-12 h-12'
          )}
        >
          <Icon
            className={cn(
              content.iconColor ?? 'text-slate-400',
              mode === 'compact' ? 'w-5 h-5' : 'w-6 h-6'
            )}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Title Row */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h4
                className={cn(
                  'font-medium text-slate-200 truncate',
                  mode === 'compact' ? 'text-sm' : 'text-base'
                )}
              >
                {content.title}
              </h4>
              {content.subtitle && (
                <p className="text-xs text-slate-500 truncate mt-0.5">
                  {content.subtitle}
                </p>
              )}
            </div>

            {/* Right side: badges and actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {showScore && (
                <ScoreBadge
                  score={result.score}
                  rerankScore={result.rerankScore}
                />
              )}
              {showSource && <SourceBadge source={result.source} />}
              {content.rightContent}
              {mode === 'expanded' && !listboxOption && !gridCell && <ActionMenu result={result} onAction={onAction} />}
            </div>
          </div>

          {/* Preview (compact: single line, expanded: multi-line) */}
          {content.preview && (content.previewMarkdown ? (
            <KnowledgeSectionMarkdown content={content.preview} interactiveLinks={false}
              className={cn('mt-1.5', mode === 'compact' ? 'max-h-6 overflow-hidden' : 'max-h-24 overflow-y-auto')} />
          ) : (
            <p
              className={cn(
                'text-sm text-slate-400 mt-1.5',
                mode === 'compact' ? 'line-clamp-1' : 'line-clamp-3'
              )}
            >
              {content.preview}
            </p>
          ))}

          {/* Metadata (expanded mode only) */}
          {mode === 'expanded' && content.metadata && content.metadata.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
              {content.metadata.map((item, i) => (
                <span key={i} className="flex items-center gap-1">
                  {item.icon && <item.icon className="w-3 h-3" />}
                  <span className="text-slate-600">{item.label}:</span>
                  <span className="text-slate-400">{item.value}</span>
                </span>
              ))}
            </div>
          )}

          {/* Tags (expanded mode only) */}
          {mode === 'expanded' && content.tags && content.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {content.tags.slice(0, 5).map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="text-xs bg-slate-800/50 border-slate-700/50 text-slate-400"
                >
                  {tag}
                </Badge>
              ))}
              {content.tags.length > 5 && (
                <Badge
                  variant="outline"
                  className="text-xs bg-slate-800/50 border-slate-700/50 text-slate-500"
                >
                  +{content.tags.length - 5} more
                </Badge>
              )}
            </div>
          )}

          {/* Custom bottom content */}
          {mode === 'expanded' && content.bottomContent && (
            <div className="mt-3">{content.bottomContent}</div>
          )}
        </div>
      </div>
    );
  }
);

// =============================================================================
// EXPORTED BASE TILE
// =============================================================================

/**
 * BaseTile component that can be used directly with a render function
 * or extended by specific tile implementations
 */
export const BaseTile = forwardRef<HTMLDivElement, BaseTileProps>(
  function BaseTile(props, ref) {
    // Default render function uses result properties directly
    const defaultRender = (result: SearchResult): BaseTileRenderProps => {
      const Icon = getSourceIcon(result.source);
      return {
        icon: Icon,
        iconColor: getSourceIconColor(result.source),
        title: result.title,
        subtitle: result.subtitle,
        preview: result.preview,
        previewMarkdown: result.type === 'document' || result.type === 'repository',
      };
    };

    return (
      <BaseTileInternal
        ref={ref}
        {...props}
        renderContent={defaultRender}
      />
    );
  }
);

// =============================================================================
// TILE FACTORY HELPER
// =============================================================================

/**
 * Helper to create a typed tile component from a render function
 */
export function createTile<T extends SearchResult>(
  displayName: string,
  renderContent: (result: T) => BaseTileRenderProps
) {
  const TileComponent = forwardRef<HTMLDivElement, BaseTileProps<T>>(
    function TileComponent(props, ref) {
      return (
        <BaseTileInternal
          ref={ref}
          {...(props as BaseTileProps)}
          renderContent={renderContent as (result: SearchResult) => BaseTileRenderProps}
        />
      );
    }
  );

  TileComponent.displayName = displayName;
  return TileComponent;
}

export default BaseTile;
